'use client';

import { useState } from 'react';
import styles from './ShippingToggle.module.css';
import { FaShippingFast } from 'react-icons/fa';
import isDateRangeInTimeSlots from './isDateRangeInTimeSlots';
import { FormattedMessage, injectIntl } from 'react-intl';
import { intlShape } from '../../../../util/reactIntl';

// Constants
const VAT_RATE = 0.21;
const ADDITIONAL_FEE = 0.2;
const ADDITIONAL_KG_PRICE = 1.1;

const weightRanges = [
  { min: 0, max: 2, price: 12.09 },
  { min: 2, max: 5, price: 12.56 },
  { min: 5, max: 10, price: 12.86 },
];

// Price calculation
const findPrice = (weight, ranges) => {
  for (const range of ranges) {
    if (weight >= range.min && weight <= range.max) return range.price;
  }
  return 'Not in range';
};

const calculateShippingAmount = listing => {
  if (!listing || !listing.attributes?.publicData?.shippingData?.isShippingActive) return 0;

  const { shippingData } = listing.attributes.publicData;
  const { shippingOption, weight, insuranceOption, shippingPrice } = shippingData || {};

  let basePrice = 0;

  if (shippingOption === 'normal') {
    const weightNumber = Number(weight);
    basePrice = shippingPrice
      ? Number(shippingPrice)
      : weightNumber >= 10
      ? 7.45 + Math.ceil(weightNumber - 10) * ADDITIONAL_KG_PRICE
      : findPrice(weightNumber, weightRanges);
  } else if (shippingOption === 'oversized') {
    basePrice = Number(shippingPrice);
  }

  let insuranceCost = 0;
  if (insuranceOption === 'additional') {
    insuranceCost = basePrice * 0.06;
  } else if (insuranceOption === 'allRisk') {
    const declaredValue = listing.attributes.publicData.estimatedValue.amount / 100;
    insuranceCost = declaredValue * 0.03;
  }

  const totalWithFee = basePrice + ADDITIONAL_FEE + insuranceCost;
  return totalWithFee * (1 + VAT_RATE);
};

// Check if a date is Mon–Thu
const isWeekdayMondayToThursday = date => {
  const day = date.getDay();
  return day >= 1 && day <= 4; // Monday = 1, Thursday = 4
};

// Get last Saturday at 00:00
const getLastSaturdayMidnight = referenceDate => {
  const day = referenceDate.getDay();
  const daysSinceSaturday = (day + 1) % 7;
  const saturday = new Date(referenceDate);
  saturday.setDate(referenceDate.getDate() - daysSinceSaturday);
  saturday.setHours(0, 0, 0, 0);
  return saturday;
};

const ShippingToggle = ({
  initialState = false,
  form,
  listing,
  onToggle,
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItems,
  intl,
}) => {
  const [isEnabled, setIsEnabled] = useState(initialState);
  console.log('lineItems:', JSON.stringify(lineItems));
  const shippingCost = calculateShippingAmount(listing);
  const totalShippingCost = shippingCost * 2;
  const shippingTotalEUR =
    ['line-item/outbound-shipping', 'line-item/return-shipping']
      .map(code => {
        const item = lineItems.find(li => li.code === code);
        return item && typeof item.lineTotal?.amount === 'number' ? item.lineTotal.amount : 0;
      })
      .reduce((sum, amount) => sum + amount, 0) / 100;

  // Round to 2 decimal places
  const shippingTotalRounded = Math.round(shippingTotalEUR * 100) / 100;

  console.log(shippingTotalRounded);

  const handleToggle = () => {
    const newState = !isEnabled;
    setIsEnabled(newState);
    if (form?.change) form.change('isShipping', newState);
    if (onToggle) onToggle(newState);
  };

  const rangeInTimeSlots = isDateRangeInTimeSlots(startDate, endDate, monthlyTimeSlots, 24);
  const { startCheck } = rangeInTimeSlots || {};

  const checkDate = start => {
    //       // ===================== UPDATED OUTBOUND SHIPMENT (48h BEFORE START) =====================
    const startDate = new Date(start);
    const day = startDate.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    let daysToSubtract;
    if (day === 0) daysToSubtract = 3;
    // Sunday → Thursday
    else if (day === 1) daysToSubtract = 4;
    // Monday → Thursday
    else if (day === 2) daysToSubtract = 5;
    // Tuesday → previous Thursday
    else if (day === 3) daysToSubtract = 2;
    // Wednesday → previous Thursday
    else if (day === 4) daysToSubtract = 2;
    // Thursday → same day
    else if (day === 5) daysToSubtract = 2;
    // Friday → Thursday
    else if (day === 6) daysToSubtract = 3; // Saturday → Thursday
    let shipmentDate = new Date(startDate);
    shipmentDate.setDate(startDate.getDate() - daysToSubtract);
    console.log(
      `OUTBOUND: ${startDate.toISOString()} -${daysToSubtract}d → ${shipmentDate.toISOString()}`
    );
    shipmentDate = shipmentDate;
    return shipmentDate;
  };
  const validStartDate = checkDate(startDate);
  const isValidStartDate = new Date(validStartDate) >= new Date();
  console.log('validStartDate  startDate', new Date(validStartDate), new Date());
  console.log('isValidStartDate', isValidStartDate);
  console.log('startCheck?.valid', startCheck?.valid);
  const twoBusinessDaysAway = isAtLeastTwoBusinessDaysAway(startDate);
  const startSunday = isSunday(startDate);
  const endSunday = isSunday(endDate);
  if (startCheck?.valid === false || startSunday || endSunday || !twoBusinessDaysAway) {
    return (
      <div className={styles.shippingUnavailableContainer}>
        <div className={styles.shippingUnavailableIcon}>
          <FaShippingFast />
        </div>
        <div className={styles.shippingUnavailableContent}>
          <h4 className={styles.shippingUnavailableTitle}>
            <FormattedMessage
              id="ShippingToggle.shippingNotAvailable"
              defaultMessage="Shipping Not Available"
            />
          </h4>
          <p className={styles.shippingUnavailableMessage}>
            <FormattedMessage id="ShippingToggle.shippingNotAvailableMessage" defaultMessage="" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shippingToggleContainer}>
      <div className={styles.toggleInfo}>
        <div className={`${styles.iconContainer} ${isEnabled ? styles.iconActive : ''}`}>
          <FaShippingFast />
        </div>
        <div className={styles.textContainer}>
          <span className={styles.title}>
            <FormattedMessage id="ShippingToggle.shipping" defaultMessage="Shipping" />
          </span>
          <p className={styles.description}>
            <FormattedMessage
              id="ShippingToggle.shippingDescription"
              defaultMessage="Delivery and return shipping"
            />
          </p>
        </div>
      </div>
      <div className={styles.toggleControls}>
        <span className={styles.price}>
          €{shippingTotalRounded ? shippingTotalRounded : totalShippingCost.toFixed(2)}
        </span>
        <div
          onClick={handleToggle}
          className={`${styles.toggleButton} ${isEnabled ? styles.toggleActive : ''}`}
          role="switch"
          aria-checked={isEnabled}
          tabIndex={0}
        >
          <span className={`${styles.toggleKnob} ${isEnabled ? styles.toggleKnobActive : ''}`} />
        </div>
      </div>
    </div>
  );
};

ShippingToggle.propTypes = {
  intl: intlShape.isRequired,
};

export default injectIntl(ShippingToggle);

function isAtLeastTwoBusinessDaysAway(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  let count = 0;
  let checkDate = new Date(today);

  // move forward until we reach 2 business days
  while (count < 2) {
    checkDate.setDate(checkDate.getDate() + 1);
    if (checkDate.getDay() !== 0) {
      // skip Sundays
      count++;
    }
  }

  // ok if target date is on/after that 2-business-days-later date
  return date.getTime() >= checkDate.getTime();
}

function isSunday(date) {
  return date.getDay() === 0;
}
